export type EncounterPhase = 'active' | 'victory' | 'failure';
export type EncounterSection = 'scout-plan' | 'passage' | 'planning-pocket' | 'warden';
export type WardenMode = 'dormant' | 'patrol' | 'telegraph' | 'recover';
export type RoutePosture = 'high' | 'center' | 'low';
export type CoverId = 'light-screen' | 'heavy-slab';
export type ApertureId = 'aperture-west' | 'aperture-east' | 'warden';
export type ExperiencePoint =
  | 'partial-scout-plan' | 'three-route-choice' | 'reciprocal-reveal' | 'route-pulse'
  | 'tether-traversal' | 'tether-manipulation' | 'two-cover-classes' | 'concealed-source'
  | 'crossfire' | 'projectile-pair' | 'cinderwake-pressure' | 'warden-finale';
export type EncounterEvent =
  | 'SCOUT PLAN READY' | 'ROUTE CHOSEN' | 'PASSAGE COMMITTED' | 'OBSTACLE BLOCKED'
  | 'ROUTE-LINE PULSE' | 'RECIPROCAL REVEAL' | 'TETHER TRAVERSAL' | 'LIGHT COVER PULLED'
  | 'HEAVY COVER PULLED' | 'PLANNING POCKET REACHED' | 'WARDEN THRESHOLD OPEN'
  | 'KILNLOCK MISS' | 'KILNLOCK HIT' | 'KILNLOCK EMPTY' | 'SHOT BLOCKED — WAIT FOR RECOVERY'
  | 'WARDEN TELEGRAPH' | 'APERTURE CHARGING' | 'BOLT AIRBORNE' | 'COVER INTERCEPT'
  | 'COVER BROKEN' | 'WARDEN STRIKE' | 'WARDEN DISPERSED' | 'CINDERWAKE WARNING' | 'ROUTE LOST';

export type Vec2 = { x: number; z: number };
export type HostileProjectile = Readonly<{
  id: number;
  sourceId: ApertureId;
  source: Vec2 & { y: number };
  target: Vec2;
  progress: number;
  height: number;
}>;
export type ApertureState = Readonly<{
  id: Exclude<ApertureId, 'warden'>;
  source: Vec2 & { y: number };
  revealed: boolean;
  revealCause: 'pulse' | 'peek' | null;
  mode: 'searching' | 'charging' | 'recover';
  elapsedMs: number;
}>;
export type CoverState = Readonly<{
  id: CoverId;
  mass: 'light' | 'heavy';
  x: number;
  z: number;
  durability: number;
  maxDurability: number;
  deployed: boolean;
  destroyed: boolean;
}>;
export type EncounterState = {
  phase: EncounterPhase;
  section: EncounterSection;
  player: Vec2 & { health: number; yaw: number; exposed: boolean };
  warden: Vec2;
  wardenHealth: number;
  wardenMode: WardenMode;
  modeElapsedMs: number;
  lastEvent: EncounterEvent;
  routePosture: RoutePosture;
  routeCommitted: boolean;
  kilnlockRounds: number;
  shotHistory: readonly ('miss' | 'blocked' | 'hit' | 'kill')[];
  threatRevealed: boolean;
  apertures: readonly ApertureState[];
  routePulse: number;
  tetherActive: boolean;
  traversalUsed: boolean;
  manipulationUsed: boolean;
  cover: readonly CoverState[];
  activeCover: CoverId | null;
  cinderwakeZ: number;
  cinderwakeStage: 'dormant' | 'advancing' | 'retreat-cut' | 'damaging' | 'paused';
  cinderwakeDamageMs: number;
  elapsedMs: number;
  projectiles: readonly HostileProjectile[];
  nextProjectileId: number;
  experience: readonly ExperiencePoint[];
};

export type EncounterOptions = Partial<{ playerHealth: number; wardenHealth: number; kilnlockRounds: number; section: EncounterSection }>;
export type EncounterInput = Partial<{
  moveX: number; moveZ: number; lookYaw: number; fire: boolean; elapsedMs: number; restart: boolean;
  routePosture: RoutePosture; pulse: boolean; tether: boolean; peek: boolean; practice: boolean;
}>;

export const ARENA = { minX: -10, maxX: 10, minZ: -10, maxZ: 18, playerRadius: 0.55 };
export const WARDEN_POSITION: Vec2 = { x: 0, z: -7 };
export const OBSTACLES = [{ minX: 2, maxX: 5, minZ: 1, maxZ: 7 }, { minX: -7, maxX: -4, minZ: -6, maxZ: -2 }];
const APERTURES: readonly ApertureState[] = [
  { id: 'aperture-west', source: { x: -4.8, y: 2.8, z: -4.8 }, revealed: false, revealCause: null, mode: 'searching', elapsedMs: 0 },
  { id: 'aperture-east', source: { x: 4.6, y: 4.2, z: -1.2 }, revealed: false, revealCause: null, mode: 'searching', elapsedMs: 0 },
];
const PROJECTILE_MS = 720;
const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));
const normAngle = (value: number) => Math.atan2(Math.sin(value), Math.cos(value));
const addExperience = (state: EncounterState, ...points: ExperiencePoint[]) => [...new Set([...state.experience, ...points])];

export function assertEncounterState(state: EncounterState): void {
  const finite = [state.player.x, state.player.z, state.player.health, state.player.yaw, state.warden.x,
    state.warden.z, state.wardenHealth, state.modeElapsedMs, state.kilnlockRounds, state.cinderwakeZ,
    state.cinderwakeDamageMs, state.elapsedMs, state.routePulse].every(Number.isFinite);
  if (!finite) throw new Error('Invalid encounter state: combat values must be finite');
  if (state.player.health < 0 || state.player.health > 4 || state.wardenHealth < 0 || state.wardenHealth > 3)
    throw new Error('Invalid encounter state: health outside bounds');
  if (state.kilnlockRounds < 0 || state.kilnlockRounds > 3 || state.shotHistory.length + state.kilnlockRounds !== 3)
    throw new Error('Invalid encounter state: three-shot ammunition ledger mismatch');
  if (state.phase === 'active' && (state.player.health === 0 || state.wardenHealth === 0))
    throw new Error('Invalid encounter state: active phase with a defeated combatant');
  if (state.phase === 'victory' && state.wardenHealth !== 0) throw new Error('Invalid encounter state: victory requires a dispersed Warden');
  if (state.phase === 'failure' && state.player.health !== 0) throw new Error('Invalid encounter state: failure requires zero route integrity');
  if (state.section !== 'warden' && state.wardenMode !== 'dormant') throw new Error('Invalid encounter state: Warden active before threshold');
  if (state.apertures.some(aperture => aperture.revealed !== (aperture.revealCause !== null))) throw new Error('Invalid encounter state: reciprocal reveal mismatch');
  if (state.cover.some(cover => cover.durability < 0 || cover.durability > cover.maxDurability)) throw new Error('Invalid encounter state: cover durability');
  for (const shot of state.projectiles) {
    if (!Number.isFinite(shot.progress) || shot.progress < 0 || shot.progress > 1 || !Number.isFinite(shot.height))
      throw new Error('Invalid encounter state: projectile pair is detached or non-finite');
  }
  if (state.phase !== 'active' && (state.projectiles.length || state.tetherActive || state.routePulse > 0))
    throw new Error('Invalid encounter state: terminal phase retains autonomous effects');
}

function insideObstacle(point: Vec2) {
  return OBSTACLES.some(box => point.x > box.minX - ARENA.playerRadius && point.x < box.maxX + ARENA.playerRadius
    && point.z > box.minZ - ARENA.playerRadius && point.z < box.maxZ + ARENA.playerRadius);
}
function segmentHitsBox(a: Vec2, b: Vec2, box: typeof OBSTACLES[number]) {
  const dx = b.x - a.x; const dz = b.z - a.z; let low = 0; let high = 1;
  for (const [origin, delta, min, max] of [[a.x, dx, box.minX, box.maxX], [a.z, dz, box.minZ, box.maxZ]] as const) {
    if (Math.abs(delta) < 0.00001) { if (origin < min || origin > max) return false; continue; }
    const t1 = (min - origin) / delta; const t2 = (max - origin) / delta;
    low = Math.max(low, Math.min(t1, t2)); high = Math.min(high, Math.max(t1, t2));
    if (low > high) return false;
  }
  return low > 0.001 && low < 0.999;
}
export function hasWardenLineOfSight(player: Vec2) { return !OBSTACLES.some(box => segmentHitsBox(player, WARDEN_POSITION, box)); }

export function createEncounter(options: EncounterOptions = {}): EncounterState {
  const section = options.section ?? 'scout-plan';
  const state: EncounterState = {
    phase: 'active', section,
    player: { x: 0, z: section === 'warden' ? -3.5 : 8, health: options.playerHealth ?? 4, yaw: 0, exposed: false },
    warden: { ...WARDEN_POSITION }, wardenHealth: options.wardenHealth ?? 3,
    wardenMode: section === 'warden' ? 'patrol' : 'dormant', modeElapsedMs: 0,
    lastEvent: section === 'warden' ? 'WARDEN THRESHOLD OPEN' : 'SCOUT PLAN READY', routePosture: 'center', routeCommitted: section !== 'scout-plan',
    kilnlockRounds: options.kilnlockRounds ?? 3, shotHistory: Array.from({ length: 3 - (options.kilnlockRounds ?? 3) }, () => 'miss' as const), threatRevealed: false,
    apertures: APERTURES.map(aperture => ({ ...aperture })), routePulse: 0, tetherActive: false,
    traversalUsed: section === 'warden', manipulationUsed: section === 'warden',
    cover: [
      { id: 'light-screen', mass: 'light', x: 2, z: 2, durability: 1, maxDurability: 1, deployed: false, destroyed: false },
      { id: 'heavy-slab', mass: 'heavy', x: 4, z: 1, durability: 4, maxDurability: 4, deployed: false, destroyed: false },
    ], activeCover: null,
    cinderwakeZ: 13, cinderwakeStage: section === 'scout-plan' ? 'dormant' : section === 'warden' ? 'paused' : 'advancing', cinderwakeDamageMs: 0,
    elapsedMs: 0, projectiles: [], nextProjectileId: 1,
    experience: section === 'warden' ? ['partial-scout-plan', 'three-route-choice', 'reciprocal-reveal', 'route-pulse', 'tether-traversal', 'tether-manipulation', 'two-cover-classes', 'concealed-source', 'crossfire', 'projectile-pair', 'cinderwake-pressure'] : ['partial-scout-plan'],
  };
  assertEncounterState(state); return state;
}

const terminal = (state: EncounterState, phase: 'victory' | 'failure', event: EncounterEvent): EncounterState => ({
  ...state, phase, player: { ...state.player, health: phase === 'failure' ? 0 : state.player.health },
  wardenHealth: phase === 'victory' ? 0 : state.wardenHealth, wardenMode: phase === 'victory' ? 'dormant' : state.wardenMode,
  projectiles: [], routePulse: 0, tetherActive: false, lastEvent: event,
});

export function stepEncounter(state: EncounterState, input: EncounterInput): EncounterState {
  assertEncounterState(state);
  if (input.restart) return createEncounter();
  if (state.phase !== 'active') return state;
  const dt = Math.max(0, input.elapsedMs ?? 0);
  let next: EncounterState = { ...state, routePulse: Math.max(0, state.routePulse - dt), tetherActive: input.tether ?? false };
  let lastEvent = state.lastEvent;

  if (input.routePosture && state.section === 'scout-plan') {
    next = { ...next, routePosture: input.routePosture, lastEvent: 'ROUTE CHOSEN', experience: addExperience(next, 'three-route-choice') };
    lastEvent = 'ROUTE CHOSEN';
  }
  if (input.pulse) {
    const unrevealed = next.apertures.find(aperture => !aperture.revealed);
    const apertures = next.apertures.map(aperture => aperture.id === unrevealed?.id
      ? { ...aperture, revealed: true, revealCause: 'pulse' as const, mode: 'charging' as const, elapsedMs: 0 }
      : aperture);
    const firstCommit = next.section === 'scout-plan';
    next = { ...next, section: firstCommit ? 'passage' : next.section, routeCommitted: true, routePulse: 650,
      apertures, threatRevealed: apertures.some(aperture => aperture.revealed), player: { ...next.player, exposed: false },
      cinderwakeStage: firstCommit ? 'advancing' : next.cinderwakeStage,
      experience: addExperience(next, 'route-pulse', 'reciprocal-reveal', 'concealed-source'),
      lastEvent: firstCommit ? 'PASSAGE COMMITTED' : 'RECIPROCAL REVEAL' };
    lastEvent = next.lastEvent;
  }
  if (input.peek && next.section === 'passage') {
    const unrevealed = next.apertures.find(aperture => !aperture.revealed);
    next = { ...next, apertures: next.apertures.map(aperture => aperture.id === unrevealed?.id ? { ...aperture, revealed: true, revealCause: 'peek' as const, mode: 'charging' as const, elapsedMs: 0 } : aperture),
      threatRevealed: true, player: { ...next.player, exposed: true }, experience: addExperience(next, 'reciprocal-reveal', 'concealed-source'), lastEvent: 'RECIPROCAL REVEAL' };
    lastEvent = 'RECIPROCAL REVEAL';
  }
  if (input.tether && next.section === 'passage') {
    if (!next.traversalUsed) {
      const routeX = next.routePosture === 'high' ? -2.3 : next.routePosture === 'low' ? 2.3 : 0;
      next = { ...next, player: { ...next.player, x: routeX, z: 2.8 }, traversalUsed: true,
        experience: addExperience(next, 'tether-traversal'), lastEvent: 'TETHER TRAVERSAL' };
      lastEvent = 'TETHER TRAVERSAL';
    } else if (!next.manipulationUsed) {
      const coverId: CoverId = next.routePosture === 'low' ? 'heavy-slab' : 'light-screen';
      next = { ...next, cover: next.cover.map(cover => cover.id === coverId ? { ...cover, x: next.player.x, z: next.player.z - 1.2, deployed: true } : cover),
        activeCover: coverId, manipulationUsed: true, experience: addExperience(next, 'tether-manipulation', 'two-cover-classes'),
        lastEvent: coverId === 'heavy-slab' ? 'HEAVY COVER PULLED' : 'LIGHT COVER PULLED' };
      lastEvent = next.lastEvent;
    }
  }

  const desired = { x: clamp(next.player.x + (input.moveX ?? 0), ARENA.minX, ARENA.maxX), z: clamp(next.player.z + (input.moveZ ?? 0), ARENA.minZ, ARENA.maxZ) };
  const blocked = insideObstacle(desired);
  let player = { ...next.player, ...(blocked ? {} : desired), yaw: input.lookYaw ?? next.player.yaw };
  if (blocked && (input.moveX || input.moveZ)) lastEvent = 'OBSTACLE BLOCKED';
  next = { ...next, player };
  if (next.section === 'passage' && next.traversalUsed && next.manipulationUsed && player.z <= -3.2) {
    next = { ...next, section: 'planning-pocket', cinderwakeStage: 'paused', projectiles: [], player: { ...player, exposed: false }, lastEvent: 'PLANNING POCKET REACHED' };
    lastEvent = 'PLANNING POCKET REACHED';
  }
  if (next.section === 'planning-pocket' && (input.tether || input.pulse || player.z <= -5)) {
    const wardenYaw = Math.atan2(next.warden.x - player.x, -(next.warden.z - player.z));
    player = { ...player, z: Math.max(-4.2, player.z), yaw: wardenYaw };
    next = { ...next, section: 'warden', wardenMode: 'patrol', modeElapsedMs: 0, projectiles: [], cinderwakeStage: 'paused',
      player, experience: addExperience(next, 'warden-finale'), lastEvent: 'WARDEN THRESHOLD OPEN' };
    lastEvent = 'WARDEN THRESHOLD OPEN';
  }

  const aim = Math.atan2(next.warden.x - player.x, -(next.warden.z - player.z));
  const aimedAtWarden = Math.abs(normAngle(aim - player.yaw)) < 0.14 && hasWardenLineOfSight(player);
  if (input.fire) {
    if (!next.kilnlockRounds) lastEvent = 'KILNLOCK EMPTY';
    else {
      const canHit = next.section === 'warden' && next.wardenMode === 'recover' && aimedAtWarden;
      const aimedButClosed = next.section === 'warden' && aimedAtWarden && next.wardenMode !== 'recover';
      const outcome = canHit ? (next.wardenHealth === 1 ? 'kill' : 'hit') : aimedButClosed ? 'blocked' : 'miss';
      const health = canHit ? next.wardenHealth - 1 : next.wardenHealth;
      next = { ...next, kilnlockRounds: next.kilnlockRounds - 1, shotHistory: [...next.shotHistory, outcome], wardenHealth: health };
      lastEvent = outcome === 'kill' ? 'WARDEN DISPERSED' : outcome === 'hit' ? 'KILNLOCK HIT' : outcome === 'blocked' ? 'SHOT BLOCKED — WAIT FOR RECOVERY' : 'KILNLOCK MISS';
      if (health === 0) return terminal({ ...next, experience: addExperience(next, 'warden-finale') }, 'victory', 'WARDEN DISPERSED');
    }
  }

  let projectiles = next.projectiles.map(shot => ({ ...shot, progress: clamp(shot.progress + dt / PROJECTILE_MS, 0, 1) }));
  const impacts = projectiles.filter(shot => shot.progress >= 1);
  projectiles = projectiles.filter(shot => shot.progress < 1);
  let cover = next.cover;
  for (const impact of impacts) {
    const deployed = cover.find(item => item.deployed && !item.destroyed && Math.abs(item.x - impact.target.x) < 1.4);
    if (deployed) {
      cover = cover.map(item => item.id === deployed.id ? { ...item, durability: Math.max(0, item.durability - 1) } : item);
      const changed = cover.find(item => item.id === deployed.id)!;
      lastEvent = changed.durability === 0 ? 'COVER BROKEN' : 'COVER INTERCEPT';
    } else {
      player = input.practice ? { ...player, health: 4 } : { ...player, health: Math.max(0, player.health - 1) };
      lastEvent = input.practice ? 'WARDEN STRIKE' : player.health ? 'WARDEN STRIKE' : 'ROUTE LOST';
    }
  }
  cover = cover.map(item => ({ ...item, destroyed: item.durability === 0 }));

  let apertures = next.apertures;
  let nextProjectileId = next.nextProjectileId;
  if (next.section === 'passage') {
    const spawned: HostileProjectile[] = [];
    apertures = apertures.map((aperture, index) => {
      if (!aperture.revealed) return aperture;
      const chargeMs = index === 0 ? 900 : 1250;
      const recoverMs = index === 0 ? 1050 : 850;
      if (aperture.mode === 'charging' && aperture.elapsedMs + dt >= chargeMs) {
        spawned.push({ id: nextProjectileId++, sourceId: aperture.id, source: aperture.source, target: { x: player.x, z: player.z }, progress: 0, height: aperture.source.y });
        return { ...aperture, mode: 'recover' as const, elapsedMs: 0 };
      }
      if (aperture.mode === 'recover' && aperture.elapsedMs + dt >= recoverMs) return { ...aperture, mode: 'charging' as const, elapsedMs: 0 };
      return { ...aperture, elapsedMs: aperture.elapsedMs + dt };
    });
    if (spawned.length) {
      projectiles = [...projectiles, ...spawned]; lastEvent = 'BOLT AIRBORNE';
      next = { ...next, experience: addExperience(next, 'projectile-pair', ...(apertures.filter(a => a.revealed).length === 2 ? ['crossfire' as const] : [])) };
    }
  }

  let wardenMode = next.wardenMode;
  let modeElapsedMs = next.modeElapsedMs;
  if (next.section === 'warden') {
    modeElapsedMs += dt;
    if (wardenMode === 'patrol' && modeElapsedMs >= 1500) { wardenMode = 'telegraph'; modeElapsedMs = 0; lastEvent = 'WARDEN TELEGRAPH'; }
    else if (wardenMode === 'telegraph' && modeElapsedMs >= 850) {
      projectiles = [...projectiles, { id: nextProjectileId++, sourceId: 'warden', source: { x: next.warden.x, y: 2.15, z: next.warden.z }, target: { x: player.x, z: player.z }, progress: 0, height: 2.15 }];
      wardenMode = 'recover'; modeElapsedMs = 0; lastEvent = 'BOLT AIRBORNE';
    } else if (wardenMode === 'recover' && modeElapsedMs >= 1100) { wardenMode = 'patrol'; modeElapsedMs = 0; }
  }

  const elapsedMs = next.elapsedMs + dt;
  let cinderwakeZ = next.cinderwakeZ;
  let cinderwakeStage = next.cinderwakeStage;
  let cinderwakeDamageMs = next.cinderwakeDamageMs;
  if (next.section === 'passage') {
    cinderwakeZ = Math.max(-4, next.cinderwakeZ - dt * .0008);
    const distance = cinderwakeZ - player.z;
    cinderwakeStage = distance <= 0 ? 'damaging' : distance < 3 ? 'retreat-cut' : 'advancing';
    cinderwakeDamageMs = cinderwakeStage === 'damaging' ? cinderwakeDamageMs + dt : 0;
    if (cinderwakeStage === 'retreat-cut') lastEvent = 'CINDERWAKE WARNING';
    if (cinderwakeDamageMs >= 1000) { player = input.practice ? { ...player, health: 4 } : { ...player, health: Math.max(0, player.health - 1) }; cinderwakeDamageMs %= 1000; lastEvent = input.practice ? 'CINDERWAKE WARNING' : player.health ? 'CINDERWAKE WARNING' : 'ROUTE LOST'; }
    next = { ...next, experience: addExperience(next, 'cinderwake-pressure') };
  }

  if (player.health === 0) return terminal({ ...next, player, projectiles, cover, apertures, nextProjectileId, wardenMode, modeElapsedMs, cinderwakeZ, cinderwakeStage, cinderwakeDamageMs, elapsedMs }, 'failure', 'ROUTE LOST');
  const result: EncounterState = { ...next, player, projectiles, cover, apertures, nextProjectileId, wardenMode, modeElapsedMs, cinderwakeZ, cinderwakeStage, cinderwakeDamageMs, elapsedMs, lastEvent };
  assertEncounterState(result); return result;
}
